/**
 * Admin Promotion Code Management Service
 * API: /api/admin/promotion-codes
 *
 * Bu servis admin panelinde promosyon kodu yönetimi için kullanılır.
 * - Kod listeleme, oluşturma, güncelleme, silme
 * - Kod aktivasyon/deaktivasyon
 * - Kullanım geçmişi takibi
 * - İstatistikler
 */

import client from '@lib/core/axios.client';
import { API_ENDPOINTS } from '@lib/constants';
import type {
  AdminPromotionCodeListParams,
  AdminPromotionCodeListResponse,
  PromotionCodeDto,
  CreatePromotionCodeDto,
  UpdatePromotionCodeDto,
  GeneratePromotionCodeRequest,
  PromotionCodeUsageParams,
  PromotionCodeUsageListResponse,
  PromotionCodeStatsDto,
  UpdateStatusesResponse,
} from '@/types/api/admin';

export class AdminPromotionCodeService {
  private static instance: AdminPromotionCodeService;

  private constructor() {}

  public static getInstance(): AdminPromotionCodeService {
    if (!AdminPromotionCodeService.instance) {
      AdminPromotionCodeService.instance = new AdminPromotionCodeService();
    }
    return AdminPromotionCodeService.instance;
  }

  // ============ Promosyon Kodu CRUD ============

  /**
   * Tüm promosyon kodlarını listele (filtreleme ve sayfalama ile)
   */
  async getPromotionCodes(
    params?: AdminPromotionCodeListParams
  ): Promise<AdminPromotionCodeListResponse> {
    return await client.get<AdminPromotionCodeListResponse>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.LIST,
      { params }
    );
  }

  /**
   * ID'ye göre promosyon kodu detayı getir
   */
  async getPromotionCodeById(id: string): Promise<PromotionCodeDto> {
    return await client.get<PromotionCodeDto>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.BY_ID(id)
    );
  }

  /**
   * Kod string'ine göre promosyon kodu detayı getir
   */
  async getPromotionCodeByCode(code: string): Promise<PromotionCodeDto> {
    return await client.get<PromotionCodeDto>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.BY_CODE(code)
    );
  }

  /**
   * Yeni promosyon kodu oluştur
   */
  async createPromotionCode(
    data: CreatePromotionCodeDto
  ): Promise<PromotionCodeDto> {
    return await client.post<PromotionCodeDto>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.CREATE,
      data
    );
  }

  /**
   * Promosyon kodunu güncelle
   */
  async updatePromotionCode(
    id: string,
    data: UpdatePromotionCodeDto
  ): Promise<PromotionCodeDto> {
    return await client.put<PromotionCodeDto>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.UPDATE(id),
      data
    );
  }

  /**
   * Promosyon kodunu sil (soft delete)
   */
  async deletePromotionCode(id: string): Promise<void> {
    return await client.delete<void>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.DELETE(id)
    );
  }

  // ============ Aktivasyon/Deaktivasyon ============

  /**
   * Promosyon kodunu aktif et
   */
  async activatePromotionCode(id: string): Promise<PromotionCodeDto> {
    return await client.patch<PromotionCodeDto>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.ACTIVATE(id)
    );
  }

  /**
   * Promosyon kodunu pasif yap
   */
  async deactivatePromotionCode(id: string): Promise<PromotionCodeDto> {
    return await client.patch<PromotionCodeDto>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.DEACTIVATE(id)
    );
  }

  // ============ Otomatik Kod Üretme ============

  /**
   * Rastgele promosyon kodu üret
   */
  async generatePromotionCode(
    data: GeneratePromotionCodeRequest
  ): Promise<PromotionCodeDto> {
    return await client.post<PromotionCodeDto>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.GENERATE,
      data
    );
  }

  // ============ Kullanım Geçmişi ============

  /**
   * Belirli bir promosyon kodunun kullanım geçmişi
   */
  async getPromotionCodeUsages(
    id: string,
    params?: { pageNumber?: number; pageSize?: number }
  ): Promise<PromotionCodeUsageListResponse> {
    return await client.get<PromotionCodeUsageListResponse>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.USAGES(id),
      { params }
    );
  }

  /**
   * Tüm promosyon kodları kullanım geçmişi (filtreleme ile)
   */
  async getAllPromotionCodeUsages(
    params?: PromotionCodeUsageParams
  ): Promise<PromotionCodeUsageListResponse> {
    return await client.get<PromotionCodeUsageListResponse>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.ALL_USAGES,
      { params }
    );
  }

  // ============ İstatistikler ============

  /**
   * Promosyon kodu istatistikleri ve analitiği
   */
  async getStatistics(): Promise<PromotionCodeStatsDto> {
    return await client.get<PromotionCodeStatsDto>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.STATISTICS
    );
  }

  // ============ Durum Güncelleme ============

  /**
   * Süresi dolmuş ve tükenmiş kodların durumlarını güncelle
   */
  async updateStatuses(): Promise<UpdateStatusesResponse> {
    return await client.post<UpdateStatusesResponse>(
      API_ENDPOINTS.ADMIN.PROMOTION_CODES.UPDATE_STATUSES
    );
  }

  // ============ Yardımcı Metodlar ============

  /**
   * Promosyon kodunun belirli bir tenant tarafından kullanılıp kullanılmadığını kontrol et
   */
  async checkTenantUsage(
    promotionCodeId: string,
    tenantId: string
  ): Promise<boolean> {
    const usages = await this.getAllPromotionCodeUsages({
      promotionCodeId,
      tenantId,
      pageSize: 1,
    });
    return usages.totalCount > 0;
  }

  /**
   * Aktif promosyon kodlarını listele
   */
  async getActivePromotionCodes(
    params?: Omit<AdminPromotionCodeListParams, 'isValid'>
  ): Promise<AdminPromotionCodeListResponse> {
    return this.getPromotionCodes({
      ...params,
      isValid: true,
    });
  }
}

export const adminPromotionCodeService = AdminPromotionCodeService.getInstance();
